/**
 * ScheduleFields — the "when" form for a scheduled run: frequency, time,
 * timezone, a weekday picker, start / optional end dates, and a plain-English
 * preview. The one timetable form (#2391), used by the Run Suite dialog's When
 * tab for both creating a schedule and editing one from Runs.
 *
 * Holds its own field state and reports the built schedule payload up through
 * `onChange` on mount and on every change. Pass a stable `onChange` (a
 * useState setter, or a useCallback) — it is in the effect's dependency list.
 */
import { useEffect, useState } from 'react'
import { Clock } from 'lucide-react'
import { Input, Label, Select } from '@/components/ui'

export type ScheduleFrequency = 'once' | 'daily' | 'weekly' | 'cron'

/** What a caller needs to create or update a scheduled run. */
export interface SchedulePayload {
  frequency: ScheduleFrequency
  cronExpression: string
  timezone: string
  startDate: string
  endDate?: string
  daysOfWeek?: number[]
}

const DAYS_OF_WEEK = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
]

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Kolkata',
  'Australia/Sydney',
]

export function buildCronExpression(frequency: ScheduleFrequency, time: string, daysOfWeek: number[], cronExpression: string, startDate: string): string {
  const [hours, minutes] = time.split(':')
  switch (frequency) {
    case 'once': {
      const [, month, day] = startDate.split('-')
      return `${minutes} ${hours} ${parseInt(day)} ${parseInt(month)} *`
    }
    case 'daily':
      return `${minutes} ${hours} * * *`
    case 'weekly': {
      // Sort a copy — `daysOfWeek` is React state; sorting it in place mutates
      // state and can desync the render (Copilot review, #2391).
      const days = daysOfWeek.length > 0 ? [...daysOfWeek].sort((a, b) => a - b).join(',') : '1'
      return `${minutes} ${hours} * * ${days}`
    }
    case 'cron':
      return cronExpression
  }
}

export function describeCron(frequency: ScheduleFrequency, time: string, timezone: string, daysOfWeek: number[], cronExpression: string, startDate: string): string {
  const tz = timezone.replace(/_/g, ' ')
  switch (frequency) {
    case 'once':
      return `Once on ${startDate} at ${time} ${tz}`
    case 'daily':
      return `Every day at ${time} ${tz}`
    case 'weekly': {
      // Copy before sorting — see buildCronExpression (Copilot review, #2391).
      const dayNames = [...daysOfWeek].sort((a, b) => a - b).map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).filter(Boolean).join(', ')
      return `Every ${dayNames || 'Monday'} at ${time} ${tz}`
    }
    case 'cron':
      return `Custom: ${cronExpression} (${tz})`
  }
}

export function parseTimeFromCron(cron: string): string {
  const parts = cron.split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`
  }
  return '09:00'
}

/** Seed values for editing an existing schedule (Runs). Omit for a new one. */
export interface ScheduleFieldsInitial {
  frequency?: ScheduleFrequency
  cronExpression?: string
  timezone?: string
  startDate?: string
  endDate?: string
  daysOfWeek?: number[]
}

export function ScheduleFields({ initial, onChange }: {
  initial?: ScheduleFieldsInitial
  onChange: (payload: SchedulePayload) => void
}) {
  const [frequency, setFrequency] = useState<ScheduleFrequency>(initial?.frequency || 'daily')
  const [time, setTime] = useState(initial?.cronExpression ? parseTimeFromCron(initial.cronExpression) : '09:00')
  const [timezone, setTimezone] = useState(initial?.timezone || 'America/New_York')
  const [startDate, setStartDate] = useState(
    initial?.startDate ? new Date(initial.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
  )
  const [endDate, setEndDate] = useState(initial?.endDate ? new Date(initial.endDate).toISOString().split('T')[0] : '')
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(initial?.daysOfWeek || [1, 2, 3, 4, 5])
  const [cronExpression, setCronExpression] = useState(
    initial?.frequency === 'cron' ? (initial.cronExpression || '0 9 * * 1-5') : '0 9 * * 1-5',
  )

  function toggleDay(day: number) {
    setDaysOfWeek(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b))
  }

  // Report the built payload up on mount and on every field change. `onChange`
  // must be stable (a setState setter) so this does not loop.
  useEffect(() => {
    const cron = buildCronExpression(frequency, time, daysOfWeek, cronExpression, startDate)
    onChange({
      frequency,
      cronExpression: cron,
      timezone,
      startDate,
      endDate: endDate || undefined,
      daysOfWeek: frequency === 'weekly' ? daysOfWeek : undefined,
    })
  }, [frequency, time, timezone, startDate, endDate, daysOfWeek, cronExpression, onChange])

  const preview = describeCron(frequency, time, timezone, daysOfWeek, cronExpression, startDate)

  return (
    <div className="space-y-4">
      {/* Frequency */}
      <div className="space-y-2">
        <Label>Frequency</Label>
        <div className="grid grid-cols-4 gap-1">
          {(['once', 'daily', 'weekly', 'cron'] as const).map(f => (
            <button
              key={f}
              type="button"
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${frequency === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              onClick={() => setFrequency(f)}
            >
              {f === 'cron' ? 'Custom' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Time (for once/daily/weekly) */}
      {frequency !== 'cron' && (
        <div className="flex gap-3">
          <div className="space-y-2 flex-1">
            <Label>Time</Label>
            <Input type="time" value={time} onChange={e => setTime(e.target.value)} />
          </div>
          <div className="space-y-2 flex-1">
            <Label>Timezone</Label>
            <Select
              options={COMMON_TIMEZONES.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              className="text-xs"
            />
          </div>
        </div>
      )}

      {/* Day of week (for weekly) */}
      {frequency === 'weekly' && (
        <div className="space-y-2">
          <Label>Days</Label>
          <div className="flex gap-1">
            {DAYS_OF_WEEK.map(d => (
              <button
                key={d.value}
                type="button"
                className={`w-9 h-9 text-xs rounded-md transition-colors ${daysOfWeek.includes(d.value) ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                onClick={() => toggleDay(d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom cron */}
      {frequency === 'cron' && (
        <div className="space-y-2">
          <Label>Cron Expression</Label>
          <Input
            value={cronExpression}
            onChange={e => setCronExpression(e.target.value)}
            placeholder="0 9 * * 1-5"
            className="font-mono text-sm"
          />
          <p className="text-[10px] text-muted-foreground">Format: minute hour day-of-month month day-of-week</p>
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select
              options={COMMON_TIMEZONES.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              className="text-xs"
            />
          </div>
        </div>
      )}

      {/* Start / End dates */}
      <div className="flex gap-3">
        <div className="space-y-2 flex-1">
          <Label>Start Date</Label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-2 flex-1">
          <Label>End Date <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
      </div>

      {/* Preview */}
      <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md border">
        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-sm">{preview}</p>
      </div>
    </div>
  )
}
